/**
 * Monitor Panel Component
 * Real-time graphing of pin values
 */

import { serial } from '../serial/serial.js';
import { notifications } from './notifications.js';
import { t } from '../utils/i18n.js';

export class MonitorPanel {
    constructor(pinsPanel) {
        this._pinsPanel = pinsPanel;
        this._canvas = document.getElementById('monitor-chart');
        this._ctx = this._canvas?.getContext('2d');
        this._checkboxContainer = document.getElementById('monitor-pin-checkboxes');

        this._isRunning = false;
        this._interval = null;
        this._pollInProgress = false;
        this._readTimeoutMs = 1000;
        this._data = {};
        this._maxPoints = 100;
        this._colors = [
            '#e53935', '#43a047', '#1e88e5', '#fb8c00',
            '#8e24aa', '#00acc1', '#7cb342', '#f4511e',
            '#5e35b1', '#00897b'
        ];

        this._renderPinCheckboxes();
        this._bindEvents();
        this._initCanvas();
    }

    _renderPinCheckboxes() {
        let html = '';
        for (let pin = 1; pin <= 10; pin++) {
            const checked = pin <= 4 ? 'checked' : '';
            html += `
                <label class="pin-checkbox">
                    <input type="checkbox" data-pin="${pin}" ${checked}>
                    <span>${pin}</span>
                </label>
            `;
        }
        this._checkboxContainer.innerHTML = html;
    }

    _bindEvents() {
        // Toggle button
        document.getElementById('btn-monitor-toggle')?.addEventListener('click', () => {
            if (this._isRunning) {
                this.stop();
            } else {
                this.start();
            }
        });

        // Clear button
        document.getElementById('btn-monitor-clear')?.addEventListener('click', () => {
            this.clear();
        });

        // Resize handling
        window.addEventListener('resize', () => {
            this._resizeCanvas();
        });
    }

    _initCanvas() {
        this._resizeCanvas();
        this._draw();
    }

    _resizeCanvas() {
        if (!this._canvas) return;

        const container = this._canvas.parentElement;
        const rect = container.getBoundingClientRect();

        // Account for padding
        this._canvas.width = rect.width - 32;
        this._canvas.height = rect.height - 32;

        this._draw();
    }

    _getSelectedPins() {
        const pins = [];
        this._checkboxContainer.querySelectorAll('input:checked').forEach(cb => {
            pins.push(parseInt(cb.dataset.pin));
        });
        return pins;
    }

    start() {
        if (this._isRunning) return;

        if (!serial.connected) {
            notifications.warning(t('disconnected'));
            return;
        }

        this._isRunning = true;
        const toggleBtn = document.getElementById('btn-monitor-toggle');
        if (toggleBtn) {
            toggleBtn.textContent = t('stop');
            toggleBtn.classList.remove('btn-secondary');
            toggleBtn.classList.add('btn-danger');
        }

        const intervalMs = parseInt(document.getElementById('monitor-interval')?.value || 500);
        // A timeout forces a reconnect because responses have no request IDs.
        // Leave enough margin to avoid disconnecting a healthy, busy device.
        this._readTimeoutMs = Math.max(1000, intervalMs * 2);

        this._interval = setInterval(() => {
            this._poll();
        }, intervalMs);
        this._poll();
    }

    stop() {
        this._isRunning = false;

        if (this._interval) {
            clearInterval(this._interval);
            this._interval = null;
        }

        const toggleBtn = document.getElementById('btn-monitor-toggle');
        if (toggleBtn) {
            toggleBtn.textContent = t('start');
            toggleBtn.classList.remove('btn-danger');
            toggleBtn.classList.add('btn-secondary');
        }
    }

    clear() {
        this._data = {};
        this._draw();
    }

    async _poll() {
        if (this._pollInProgress) {
            return;
        }

        if (!serial.connected || !this._isRunning) {
            this.stop();
            return;
        }

        this._pollInProgress = true;

        try {
            const pins = this._getSelectedPins();
            const dataType = document.getElementById('monitor-data-type')?.value || 'digital';

            for (const pin of pins) {
                if (!serial.connected || !this._isRunning) {
                    break;
                }

                try {
                    const value = await serial.readPin(pin, this._readTimeoutMs);
                    let numericValue = 0;

                    if (value !== null && value !== undefined) {
                        if (dataType === 'digital') {
                            numericValue = parseInt(value) || 0;
                        } else if (dataType === 'analog') {
                            numericValue = parseInt(value) || 0;
                        } else if (dataType === 'capture') {
                            // Parse frequency from capture format: freq,duty
                            const match = value.match(/([0-9]+)/);
                            if (match) {
                                numericValue = parseInt(match[1]) || 0;
                            }
                        }
                    }

                    // Store data
                    if (!this._data[pin]) {
                        this._data[pin] = [];
                    }
                    this._data[pin].push({
                        time: Date.now(),
                        value: numericValue
                    });

                    // Limit data points
                    if (this._data[pin].length > this._maxPoints) {
                        this._data[pin].shift();
                    }
                } catch (error) {
                    // Skip errors silently
                }
            }

            this._draw();
        } finally {
            this._pollInProgress = false;
        }
    }

    _draw() {
        if (!this._ctx) return;

        const width = this._canvas.width;
        const height = this._canvas.height;
        const padding = { top: 20, right: 20, bottom: 30, left: 50 };
        const chartWidth = width - padding.left - padding.right;
        const chartHeight = height - padding.top - padding.bottom;

        // Get theme colors
        const style = getComputedStyle(document.documentElement);
        const bgColor = style.getPropertyValue('--bg-subtle').trim() || '#18181b';
        const textColor = style.getPropertyValue('--text-muted').trim() || '#706f78';
        const gridColor = style.getPropertyValue('--border-subtle').trim() || '#28282c';

        // Clear canvas
        this._ctx.fillStyle = bgColor;
        this._ctx.fillRect(0, 0, width, height);

        // Find data range
        let minValue = 0;
        let maxValue = 1;
        const dataType = document.getElementById('monitor-data-type')?.value || 'digital';

        if (dataType === 'analog') {
            maxValue = 4095;
        } else if (dataType === 'capture') {
            maxValue = 1000000; // 1 MHz default max
        }

        // Adjust max based on actual data
        for (const pin in this._data) {
            for (const point of this._data[pin]) {
                if (point.value > maxValue * 0.8) {
                    maxValue = point.value * 1.2;
                }
            }
        }

        // Draw grid
        this._ctx.strokeStyle = gridColor;
        this._ctx.lineWidth = 1;

        // Horizontal grid lines
        const gridLines = 5;
        for (let i = 0; i <= gridLines; i++) {
            const y = padding.top + (chartHeight / gridLines) * i;
            this._ctx.beginPath();
            this._ctx.moveTo(padding.left, y);
            this._ctx.lineTo(width - padding.right, y);
            this._ctx.stroke();

            // Labels
            const value = maxValue - (maxValue / gridLines) * i;
            this._ctx.fillStyle = textColor;
            this._ctx.font = '10px sans-serif';
            this._ctx.textAlign = 'right';
            this._ctx.fillText(value.toFixed(0), padding.left - 5, y + 3);
        }

        // Draw data lines
        const pins = this._getSelectedPins();
        for (let i = 0; i < pins.length; i++) {
            const pin = pins[i];
            const data = this._data[pin];
            if (!data || data.length < 2) continue;

            this._ctx.strokeStyle = this._colors[pin - 1];
            this._ctx.lineWidth = 2;
            this._ctx.beginPath();

            for (let j = 0; j < data.length; j++) {
                const x = padding.left + (chartWidth / this._maxPoints) * j;
                const y = padding.top + chartHeight - (data[j].value / maxValue) * chartHeight;

                if (j === 0) {
                    this._ctx.moveTo(x, y);
                } else {
                    this._ctx.lineTo(x, y);
                }
            }

            this._ctx.stroke();
        }

        // Draw legend
        this._ctx.textAlign = 'left';
        let legendX = padding.left;
        for (let i = 0; i < pins.length; i++) {
            const pin = pins[i];
            this._ctx.fillStyle = this._colors[pin - 1];
            this._ctx.fillRect(legendX, height - 15, 12, 12);
            this._ctx.fillStyle = textColor;
            this._ctx.fillText(`Pin ${pin}`, legendX + 16, height - 5);
            legendX += 70;
        }
    }

    destroy() {
        this.stop();
    }
}
